import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { HttpBaseService } from './http-base.service';
import { PermissionService } from './permission.service';

export interface AuthUser {
  email: string;
  name: string;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

interface RefreshResponse {
  access_token: string;
  token_type: string;
}

const AUTH_STORAGE_KEY = 'gf-web.auth-user';
const AUTH_TOKENS_STORAGE_KEY = 'gf-web.auth-tokens';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly http = inject(HttpBaseService);
  private readonly permission = inject(PermissionService);
  private readonly userState = signal<AuthUser | null>(this.restoreUser());
  private readonly tokensState = signal<LoginResponse | null>(this.restoreTokens());
  private refreshInFlight: Promise<string | null> | null = null;

  readonly user = this.userState.asReadonly();
  readonly isAuthenticated = () => this.tokensState() !== null;

  async login(email: string, password: string): Promise<void> {
    const response = await firstValueFrom(
      this.http.post<LoginResponse>('/auth/login', {
        email,
        password,
        platform: 'web',
      }),
    );

    const user: AuthUser = {
      email,
      name: email.split('@')[0].replace(/[._-]/g, ' ') || 'Demo User',
    };

    this.tokensState.set(response);
    this.userState.set(user);
    localStorage.setItem(AUTH_TOKENS_STORAGE_KEY, JSON.stringify(response));
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));

    this.permission.load().subscribe();
  }

  logout(): void {
    this.tokensState.set(null);
    this.userState.set(null);
    this.permission.clear();
    localStorage.removeItem(AUTH_TOKENS_STORAGE_KEY);
    localStorage.removeItem(AUTH_STORAGE_KEY);
    void this.router.navigate(['/login']);
  }

  getAccessToken(): string | null {
    return this.tokensState()?.access_token ?? null;
  }

  getTokenType(): string {
    return this.tokensState()?.token_type || 'Bearer';
  }

  getRefreshToken(): string | null {
    return this.tokensState()?.refresh_token ?? null;
  }

  loadPermissionsIfAuthenticated(): void {
    if (this.isAuthenticated() && !this.permission.isLoaded()) {
      this.permission.load().subscribe();
    }
  }

  async refreshAccessToken(): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      this.logout();
      return null;
    }

    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = firstValueFrom(
      this.http.post<RefreshResponse>('/auth/refresh', {
        refresh_token: refreshToken,
      }),
    )
      .then((response) => {
        const currentTokens = this.tokensState();
        if (!currentTokens) {
          return null;
        }

        const nextTokens: LoginResponse = {
          ...currentTokens,
          access_token: response.access_token,
          token_type: response.token_type,
        };

        this.tokensState.set(nextTokens);
        localStorage.setItem(AUTH_TOKENS_STORAGE_KEY, JSON.stringify(nextTokens));
        return nextTokens.access_token;
      })
      .catch(() => {
        this.logout();
        return null;
      })
      .finally(() => {
        this.refreshInFlight = null;
      });

    return this.refreshInFlight;
  }

  private restoreUser(): AuthUser | null {
    const storedValue = localStorage.getItem(AUTH_STORAGE_KEY);
    if (!storedValue) {
      return null;
    }

    try {
      return JSON.parse(storedValue) as AuthUser;
    } catch {
      localStorage.removeItem(AUTH_STORAGE_KEY);
      return null;
    }
  }

  private restoreTokens(): LoginResponse | null {
    const storedValue = localStorage.getItem(AUTH_TOKENS_STORAGE_KEY);
    if (!storedValue) {
      return null;
    }

    try {
      return JSON.parse(storedValue) as LoginResponse;
    } catch {
      localStorage.removeItem(AUTH_TOKENS_STORAGE_KEY);
      return null;
    }
  }
}
